/**
 * Which freehand floor-plan gestures are offered.
 *
 * **Policy: a gesture is enabled only when it maps onto the typed
 * measurement pathway** (`utils/wallStretch.js` — pick a wall length, the plan
 * cuts at a draggable boundary and translates everything past it rigidly, so
 * every angle is preserved by construction). A gesture that edits geometry
 * some other way has to invent its own answer for what happens to the walls it
 * disturbs, and those answers kept turning out to have holes:
 *
 * - a partition meeting a moved wall's BODY is in no junction with it, so it
 *   strands — 159 such T-junctions across 323 walls in the corpus, in 14 of 16
 *   structures;
 * - two endpoints 15-30 cm apart never cluster into one junction, so they
 *   silently disconnect (44 such pairs in the corpus);
 * - a room wall on a re-cut structure wall inherits one of that wall's two
 *   endpoint offsets rather than being re-projected onto its new line.
 *
 * Rather than keep patching, the gestures are withdrawn and come back one at a
 * time, each only once it is expressed IN the typed pathway. Flipping a flag
 * back is not enough on its own — the note beside it says what has to be true.
 *
 * Full background: `reference-docs/wall-drag/overview.md`.
 */

/**
 * Dragging a wall's POSITION (translating the whole wall body).
 *
 * **Off.** It is not a length edit, so it has no expression in the typed
 * pathway at all: it re-proportions the rooms either side rather than
 * translating half the plan. Constraining it to the wall's own normal makes it
 * exact for a rectilinear plan — measured: nothing re-angles, room walls stay
 * exactly on their line — but that still leaves the T-junction and
 * offset-endpoint holes above, which the normal constraint does nothing for.
 *
 * To re-enable: it needs a primitive of its own in the framework (offset —
 * §3 of `claude-docs/2026-09-02-slanted-wall-measurement-editing-design.md`)
 * plus an answer for points lying on a moved wall's body.
 */
export const WALL_BODY_DRAG_ENABLED = false;

/**
 * Dragging a wall's END HANDLE.
 *
 * **On, routed through the typed pathway.** The cursor is projected onto the
 * wall's own axis and supplies a LENGTH; from there it is the identical edit
 * to typing that number — same plan, same boundary rectangle, same locks,
 * same storey scope, same Confirm. The handle can no longer re-angle a wall,
 * which is what made it unsafe: holding one endpoint and moving the other
 * re-angles every wall at the moved junction by construction.
 */
export const WALL_ENDPOINT_DRAG_ENABLED = true;
